import { XLogo } from "@phosphor-icons/react";
import { useLocation, useParams } from "react-router-dom";

import { LeadLabels } from "features/labels/components";

import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import {
  About,
  LeadHeading,
  LeadNotes,
  LeadOf,
  LeadSectionContainer,
  LeadSource,
  LinkedInSubscription,
  MessageLeadButton,
} from "common/components/leads";
import RestrictedComponent from "common/components/RestrictedComponent";
import { Button } from "common/components/ui/Button";
import Skeleton from "common/components/ui/Skeleton";
import {
  ContentContainer,
  ContentHeader,
  ContentScroll,
  PageTitle,
} from "common/layouts/WorkspaceLayout";

import useLead from "../../datahooks/useLead";
import Certifications from "./Certifications";
import CurrentExperience from "./CurrentExperience";
import Education from "./Education";
import Experience from "./Experience";
import Languages from "./Languages";
import Skills from "./Skills";
import Volunteering from "./Volunteering";

import type { ReactNode } from "react";

export default function LeadDetails() {
  const { profileId } = useParams();
  const { state } = useLocation();
  const { lead, refetchLead, leadError, isLoadingLead } = useLead({
    profileId,
    placeholderLead: state?.lead,
  });
  let mainContent: ReactNode;

  if (leadError) {
    mainContent = (
      <ErrorPlaceholder
        errorMessage="Could not display Leads Data"
        onRetry={refetchLead}
      />
    );
  } else {
    mainContent = (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[352px_auto]">
        <div className="flex h-fit flex-col rounded-5xl border border-black-200">
          <div className="flex flex-col p-4 gap-y-3 border-b border-b-black-200">
            <LeadHeading lead={lead} isLoading={isLoadingLead} size="lg" />
            <div className="flex gap-x-2.5">
              <MessageLeadButton
                lead={lead}
                isLoading={isLoadingLead}
                size="md"
              />
              {!!lead?.twitter_handles.length && (
                <Button asChild variant="ghost-black" intent="iconOnly">
                  <a
                    href={`https://twitter.com/${lead.twitter_handles[0]}`}
                    aria-label="go to twitter page"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <XLogo />
                  </a>
                </Button>
              )}
            </div>
          </div>
          {lead?.origins && <LeadSource origins={lead?.origins} />}
          <LinkedInSubscription
            isLoading={isLoadingLead}
            hasPremium={lead?.premium}
          />
          {lead?.lead_of && <LeadOf leadOf={lead.lead_of} />}
          <LeadSectionContainer title="Labels" isColumn hasBorder>
            {isLoadingLead ? (
              <Skeleton className="h-6 w-20" />
            ) : (
              <LeadLabels
                labels={lead.labels}
                profileId={lead.id}
                align="start"
              />
            )}
          </LeadSectionContainer>
          <About lead={lead} isLoading={isLoadingLead} hasBorder />
          {lead && (
            <RestrictedComponent visibleForPermissions={["leads_full"]}>
              <LeadSectionContainer isColumn title="Notes" hasBorder={false}>
                <LeadNotes notes={lead.notes} profileId={lead.id} />
              </LeadSectionContainer>
            </RestrictedComponent>
          )}
        </div>
        <div className="flex flex-col">
          <CurrentExperience
            currentExperience={lead?.current_experience}
            isLoading={isLoadingLead}
          />
          <Experience
            workExperience={lead?.work_experience}
            isLoading={isLoadingLead}
          />
          <Education education={lead?.education} isLoading={isLoadingLead} />
          {lead && (
            <>
              {!!lead.volunteer_experience.length && (
                <Volunteering volunteering={lead.volunteer_experience} />
              )}
              {!!lead.certifications.length && (
                <Certifications certification={lead.certifications} />
              )}
              {!!lead.skills.length && <Skills skills={lead.skills} />}
              {!!lead.languages.length && (
                <Languages languages={lead.languages} />
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <ContentContainer>
      <ContentHeader>
        <PageTitle>
          {lead ? `${lead.full_name}'s profile` : "Lead profile"}
        </PageTitle>
      </ContentHeader>
      <ContentScroll className="p-4">{mainContent}</ContentScroll>
    </ContentContainer>
  );
}
